/**
 * 文件作用：任务轮询Hook（改造版）
 * - 使用 models.config.ts 中的 polling 配置驱动轮询间隔、状态字段路径、终态判定与成功规则
 * - 优先使用模板构造 baseurl+endpoint 来查询（buildStatusUrlFromTemplate），遵循中转查询规范
 * - 保留对旧有 task.statusUrl 的回退兼容，但不作为首选
 * - 为后续新增 provider 留出扩展点
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTaskManager, TaskStatus, type Task } from './useTaskManager';
import { getJson } from '../api/client';
import type { GenerationTaskResult } from '../types/generation';
import { DEFAULT_POLL_POLICY } from '../config/polling.config';
import { buildStatusUrlFromTemplate } from '../utils/modelRouting';
import { readValueByPath } from '../utils/jsonPath';
import {
  detectTaskFailure,
  parseTaskProgress,
  type FailureDetectionOptions,
  type FailureDetectionResult,
} from '../utils/taskPollingUtils';
import { isRecord, toStringSafe } from '../utils/typeGuards';
import type { AdapterContext, AdapterStatusQuery, TaskType, GenerationAdapter } from '../adapters/types';
import { findModelConfig } from '../config/models.config';
import { useHistoryStore, type UpdateHistoryEntryPayload } from '../store/historyStore';
import { isFormDataLike } from '../utils/http';
import { resolveApiEndpoint } from '../utils/apiConfig';
import { useTaskSelfStore } from '../store/taskSelfStore';
import { getAllPendingTasks, removePendingTask } from '@/features/canvas/utils/pendingTaskStorage';

export { detectTaskFailure, detectTaskFailure as detectFailureState };
export type { FailureDetectionOptions, FailureDetectionResult };

type PollEntry = {
  timerId: ReturnType<typeof setInterval> | null;
  attempts: number;
  consecutiveErrors: number;
};

type PollConfig = {
  modelKey?: string;
  provider?: string;
  statusEndpointTemplate?: string;
  /** 任务成功判定的单一路径（向后兼容字段） */
  successPath?: string;
  /** 任务成功判定的候选路径列表（若存在会与 successPath 一并尝试） */
  successFieldPaths?: string[];
  /** 状态字段的成功值列表，当 successFieldPaths 包含状态路径时用于精确匹配 */
  successStatuses?: string[];
  progressFieldPath?: string;
  statusFieldPaths?: string[];
  failureStatuses?: string[];
  failureMessagePaths?: string[];
};

type ResolvedPollConfig = {
  config: PollConfig | null;
  adapter?: GenerationAdapter;
  disabled: boolean;
};

const buildAdapterContext = (model?: string, provider?: string): AdapterContext | undefined => {
  const resolvedModel = toStringSafe(model) ?? undefined;
  const resolvedProvider = toStringSafe(provider) ?? resolvedModel;
  if (!resolvedModel && !resolvedProvider) {
    return undefined;
  }

  const identifierCandidates = Array.from(
    new Set(
      [resolvedModel, resolvedProvider]
        .map((value) => (typeof value === 'string' && value.trim().length > 0 ? value : undefined))
        .filter((value): value is string => Boolean(value))
    )
  );

  let resolvedTaskType: TaskType | undefined;
  for (const identifier of identifierCandidates) {
    const config = findModelConfig(identifier);
    if (config) {
      resolvedTaskType = config.defaultTaskType;
      break;
    }
  }

  return {
    provider: resolvedProvider || 'unknown',
    taskType: resolvedTaskType ?? ('text' as TaskType),
    model: resolvedModel,
  };
};

const extractPollConfigFromModelConfig = (identifier?: string | null): ResolvedPollConfig => {
  if (!identifier) {
    return { config: null, adapter: undefined, disabled: false };
  }

  const modelConfig = findModelConfig(identifier);
  if (!modelConfig || !modelConfig.polling) {
    return { config: null, adapter: modelConfig?.adapter, disabled: false };
  }

  const polling = modelConfig.polling;
  if (polling.enabled === false) {
    return { config: null, adapter: modelConfig.adapter, disabled: true };
  }

  const successFieldPaths = Array.isArray(polling.successFieldPaths)
    ? polling.successFieldPaths
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0)
    : [];

  const hasSuccessPath =
    typeof polling.successPath === 'string' && polling.successPath.trim().length > 0;

  if (!hasSuccessPath && successFieldPaths.length === 0) {
    return { config: null, adapter: modelConfig.adapter, disabled: false };
  }

  const statusFieldPaths = Array.isArray(polling.statusFieldPaths)
    ? polling.statusFieldPaths
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0)
    : [];
  const failureStatuses = Array.isArray(polling.failureStatuses)
    ? polling.failureStatuses
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0)
    : [];
  const failureMessagePaths = Array.isArray(polling.failureMessagePaths)
    ? polling.failureMessagePaths
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0)
    : [];
  const successStatuses = Array.isArray(polling.successStatuses)
    ? polling.successStatuses
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0)
    : [];

  return {
    config: {
      modelKey: modelConfig.modelKey,
      provider: modelConfig.modelKey,
      statusEndpointTemplate: polling.statusEndpointTemplate,
      successPath: hasSuccessPath ? polling.successPath!.trim() : undefined,
      successFieldPaths: successFieldPaths.length > 0 ? successFieldPaths : undefined,
      successStatuses: successStatuses.length > 0 ? successStatuses : undefined,
      progressFieldPath: polling.progressPath,
      statusFieldPaths: statusFieldPaths.length > 0 ? statusFieldPaths : undefined,
      failureStatuses: failureStatuses.length > 0 ? failureStatuses : undefined,
      failureMessagePaths: failureMessagePaths.length > 0 ? failureMessagePaths : undefined,
    },
    adapter: modelConfig.adapter,
    disabled: false,
  };
};

const normalizeTaskResult = (value: unknown): GenerationTaskResult => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (isFormDataLike(value)) {
    return value;
  }
  if (isRecord(value)) {
    return value as GenerationTaskResult;
  }
  console.warn('状态查询返回了无法识别的结果结构，已回退为 null。', value);
  return null;
};

const cleanupPendingTaskByTaskId = (taskId: string) => {
  const pendingTasks = getAllPendingTasks();
  const matching = pendingTasks.find((pt) => pt.taskId === taskId);
  if (matching) {
    removePendingTask(matching.nodeGraphId, matching.nodeId);
  }
};

export const useTaskPolling = () => {
  const managerTasks = useTaskManager((s) => s.tasks);
  const taskSelfRecords = useTaskSelfStore((s) => s.records);
  const refetchTaskSelf = useTaskSelfStore((s) => s.refetch);
  const updateHistoryEntry = useHistoryStore((s) => s.updateEntry);
  const pollingIntervals = useRef<Record<string, PollEntry>>({});
  const inFlightTasks = useRef<Set<string>>(new Set());

  const applyTaskUpdate = useCallback((taskId: string, updates: Partial<Task>) => {
    const { getTask, updateTask } = useTaskManager.getState();
    const current = getTask(taskId);
    if (!current) return;

    let changed = false;
    for (const key in updates) {
      if (!Object.prototype.hasOwnProperty.call(updates, key)) continue;
      const nextValue = (updates as unknown as Record<string, unknown>)[key];
      const prevValue = (current as unknown as Record<string, unknown>)[key];
      if (prevValue !== nextValue) {
        changed = true;
        break;
      }
    }

    if (changed) {
      updateTask(taskId, updates);
    }
  }, []);

  const getHistoryIdFromTask = useCallback((task: Task): string | undefined => {
    const payload = task.payload;
    if (!isRecord(payload)) {
      return undefined;
    }
    const meta = isRecord(payload.meta) ? (payload.meta as Record<string, unknown>) : undefined;
    const historyId = meta?._historyId;
    return typeof historyId === 'string' ? historyId : undefined;
  }, []);

  const applyHistoryUpdate = useCallback(
    (task: Task, payload: UpdateHistoryEntryPayload) => {
      const historyId = getHistoryIdFromTask(task);
      if (historyId) {
        updateHistoryEntry(historyId, payload);
      }
    },
    [getHistoryIdFromTask, updateHistoryEntry]
  );

  const combinedTasks = useMemo(() => {
    const map = new Map<string, Task>();
    managerTasks.forEach((task) => {
      map.set(task.id, task);
    });
    taskSelfRecords.forEach((record) => {
      if (!map.has(record.task.id)) {
        map.set(record.task.id, record.task);
      }
    });
    return Array.from(map.values());
  }, [managerTasks, taskSelfRecords]);

  const selfTaskIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    selfTaskIdsRef.current = new Set(taskSelfRecords.map((record) => record.task.id));
  }, [taskSelfRecords]);

  const triggerTaskSelfRefreshIfNeeded = useCallback(
    (taskId: string, nextStatus?: TaskStatus) => {
      if (!nextStatus) return;
      if (!selfTaskIdsRef.current.has(taskId)) return;
      if (nextStatus !== TaskStatus.SUCCESS && nextStatus !== TaskStatus.FAILED) {
        return;
      }
      void refetchTaskSelf();
    },
    [refetchTaskSelf]
  );

  const applyTaskUpdateAndSync = useCallback(
    (task: Task, updates: Partial<Task>) => {
      applyTaskUpdate(task.id, updates);
      triggerTaskSelfRefreshIfNeeded(task.id, updates.status);
    },
    [applyTaskUpdate, triggerTaskSelfRefreshIfNeeded]
  );

  useEffect(() => {
    return () => {
      Object.values(pollingIntervals.current).forEach((entry) => {
        if (entry.timerId !== null) {
          clearInterval(entry.timerId);
        }
      });
      pollingIntervals.current = {};
      inFlightTasks.current.clear();
    };
  }, []);

  useEffect(() => {
    combinedTasks.forEach((task) => {
      if (task.status === TaskStatus.SUCCESS || task.status === TaskStatus.FAILED) {
        const existing = pollingIntervals.current[task.id];
        if (existing && existing.timerId !== null) {
          clearInterval(existing.timerId);
        }
        delete pollingIntervals.current[task.id];
        inFlightTasks.current.delete(task.id);
        return;
      }

      if (pollingIntervals.current[task.id]) return;

      (async () => {
        const { config: configPoll, adapter, disabled } = extractPollConfigFromModelConfig(task.model);
        if (disabled) {
          return;
        }

        const pollConfig = configPoll;
        if (!pollConfig) {
          console.warn(`任务 ${task.id} 未配置轮询信息，已跳过自动轮询。`);
          return;
        }

        const requestId = toStringSafe(task.requestId) ?? (task.requestId != null ? String(task.requestId) : undefined);
        const adapterCtx = buildAdapterContext(pollConfig.modelKey, pollConfig.provider ?? pollConfig.modelKey);

        const templateResolution = buildStatusUrlFromTemplate(
          pollConfig.statusEndpointTemplate,
          requestId,
          adapterCtx,
          { silent: true }
        );

        const rawFromTemplate = templateResolution?.raw;
        const resolvedFromTemplate = templateResolution?.resolved;

        const taskStatusUrlValue =
          typeof task.statusUrl === 'string' && task.statusUrl.trim().length > 0
            ? task.statusUrl.trim()
            : undefined;

        const resolvedFromTask = taskStatusUrlValue ? resolveApiEndpoint(taskStatusUrlValue) : undefined;

        const preferredEndpoint =
          rawFromTemplate ??
          taskStatusUrlValue ??
          resolvedFromTemplate ??
          resolvedFromTask;

        let resolvedEndpoint = resolvedFromTemplate ?? resolvedFromTask;
        if (!resolvedEndpoint && preferredEndpoint) {
          try {
            resolvedEndpoint = resolveApiEndpoint(String(preferredEndpoint));
          } catch (resolveError) {
            console.warn('useTaskPolling: 解析状态查询地址失败，已回退未解析地址。', resolveError);
          }
        }

        const statusQuery: AdapterStatusQuery | undefined = preferredEndpoint
          ? {
              endpoint: String(preferredEndpoint),
              resolvedEndpoint: resolvedEndpoint ? String(resolvedEndpoint) : undefined,
              requestId,
              resolveEndpoint: !resolvedEndpoint,
            }
          : undefined;

        const fetchStatusUrl = resolvedEndpoint ?? statusQuery?.endpoint;

        if (!statusQuery?.endpoint || !fetchStatusUrl) {
          console.warn(
            '缺少可用的状态查询端点（未提供模板或 requestId，且任务未包含 statusUrl），已跳过本次轮询启动，等待端点补充后重试。'
          );
          const existingEntry = pollingIntervals.current[task.id];
          if (existingEntry?.timerId) {
            clearInterval(existingEntry.timerId);
          }
          delete pollingIntervals.current[task.id];
          inFlightTasks.current.delete(task.id);
          return;
        }

        const successPath = pollConfig.successPath;
        const progressPath = pollConfig.progressFieldPath;
        const intervalMs = DEFAULT_POLL_POLICY.intervalMs ?? 2000;
        const maxAttempts = DEFAULT_POLL_POLICY.maxAttempts ?? 90;
        const adapterQueryStatus = adapter?.queryStatus;

        const fetchStatusPayload = async (): Promise<unknown> => {
          if (adapterQueryStatus && statusQuery) {
            try {
              const adapterResult = await adapterQueryStatus(statusQuery, {
                context: adapterCtx,
                requestId,
              });
              if (adapterResult !== undefined) {
                return adapterResult;
              }
            } catch (adapterError) {
              console.warn(`适配器状态查询失败，已回退至通用请求。模型: ${pollConfig.modelKey ?? 'unknown'}`, adapterError);
            }
          }
          return getJson<unknown>(fetchStatusUrl);
        };

        const pollOnce = async () => {
          if (inFlightTasks.current.has(task.id)) {
            return;
          }
          inFlightTasks.current.add(task.id);

          try {
            const rawData = await fetchStatusPayload();
            const taskResult = normalizeTaskResult(rawData);
            // 任务成功判定：依次尝试 successPath 与 successFieldPaths 中的路径，任意命中即视为成功
            const successPaths: string[] = [];
            if (successPath) {
              successPaths.push(successPath);
            }
            if (Array.isArray(pollConfig.successFieldPaths)) {
              for (const path of pollConfig.successFieldPaths) {
                if (typeof path === 'string' && path.trim().length > 0) {
                  successPaths.push(path.trim());
                }
              }
            }

            let successValue: unknown = undefined;
            const hasSuccessStatuses = Array.isArray(pollConfig.successStatuses) && pollConfig.successStatuses.length > 0;
            for (const path of successPaths) {
              const value = readValueByPath(rawData, path, { silent: true });
              // 成功判定：值必须非空（undefined/null/空字符串均视为未完成）
              if (value !== undefined && value !== null && value !== '') {
                // 如果配置了 successStatuses，检查值是否为成功状态
                if (hasSuccessStatuses) {
                  const valueStr = typeof value === 'string' ? value : String(value);
                  if (pollConfig.successStatuses!.includes(valueStr)) {
                    successValue = value;
                    break;
                  }
                  // 值存在但不是成功状态，继续检查下一个路径
                } else {
                  // 没有配置 successStatuses，只要有非空值即成功（向后兼容）
                  successValue = value;
                  break;
                }
              }
            }
            if (successValue !== undefined && successValue !== null && successValue !== '') {
              // 成功时重置错误计数
              const pollEntry = pollingIntervals.current[task.id];
              if (pollEntry) {
                pollEntry.consecutiveErrors = 0;
              }
              applyTaskUpdateAndSync(task, { status: TaskStatus.SUCCESS, result: taskResult, progress: 100 });
              // 清理画板节点的持久化任务记录
              if (task.requestId) {
                cleanupPendingTaskByTaskId(task.requestId);
              }
              applyHistoryUpdate(task, {
                status: 'success',
                output: { rawResponse: taskResult },
              });
              return;
            }

            const failureDetection = detectTaskFailure(rawData, {
              statusPaths: pollConfig.statusFieldPaths,
              failureStatuses: pollConfig.failureStatuses,
              failureMessagePaths: pollConfig.failureMessagePaths,
            });

            if (failureDetection.failed) {
              const errorMsg = failureDetection.message ?? '任务已失败';
              applyTaskUpdateAndSync(task, {
                status: TaskStatus.FAILED,
                error: errorMsg,
              });
              if (task.requestId) {
                cleanupPendingTaskByTaskId(task.requestId);
              }
              applyHistoryUpdate(task, { status: 'failed', error: errorMsg });
              return;
            }

            let currentProgress = task.progress ?? 0;
            const progressValue = progressPath
              ? readValueByPath(rawData, progressPath, { silent: true, parseJsonString: false })
              : isRecord(rawData)
                ? rawData.progress
                : undefined;
            const parsedProgress = parseTaskProgress(progressValue);
            if (typeof parsedProgress === 'number') {
              currentProgress = Math.max(0, Math.min(99, parsedProgress));
            } else {
              currentProgress = Math.min(90, currentProgress + 10);
            }

            // 运行中状态也重置错误计数(表示轮询成功)
            const pollEntry = pollingIntervals.current[task.id];
            if (pollEntry) {
              pollEntry.consecutiveErrors = 0;
            }
            applyTaskUpdateAndSync(task, { status: TaskStatus.RUNNING, progress: currentProgress });
          } catch (error) {
            const pollEntry = pollingIntervals.current[task.id];
            if (!pollEntry) {
              inFlightTasks.current.delete(task.id);
              return;
            }

            pollEntry.consecutiveErrors = (pollEntry.consecutiveErrors || 0) + 1;

            // 允许最多 3 次连续网络错误,使用指数退避
            const MAX_CONSECUTIVE_ERRORS = 3;
            if (pollEntry.consecutiveErrors <= MAX_CONSECUTIVE_ERRORS) {
              const backoffMs = Math.min(1000 * Math.pow(2, pollEntry.consecutiveErrors - 1), 8000);
              console.warn(
                `轮询任务 ${task.id} 遇到错误(第${pollEntry.consecutiveErrors}次),将在 ${backoffMs}ms 后重试:`,
                error
              );
              // 不立即标记失败,等待下次轮询
              inFlightTasks.current.delete(task.id);
              return;
            }

            // 超过重试次数才标记失败
            console.error(`轮询任务 ${task.id} 连续失败 ${pollEntry.consecutiveErrors} 次,已停止:`, error);
            const errorMsg = error instanceof Error ? error.message : '轮询失败';
            applyTaskUpdateAndSync(task, {
              status: TaskStatus.FAILED,
              error: errorMsg,
            });
            if (task.requestId) {
              cleanupPendingTaskByTaskId(task.requestId);
            }
            applyHistoryUpdate(task, { status: 'failed', error: errorMsg });
          } finally {
            inFlightTasks.current.delete(task.id);
          }
        };

        const entry: PollEntry = { timerId: null, attempts: 0, consecutiveErrors: 0 };

        const runOnceAndSchedule = async () => {
          entry.attempts += 1;
          if (entry.attempts > maxAttempts) {
            const timeoutMsg = '任务轮询超时（超过最大尝试次数）';
            applyTaskUpdateAndSync(task, { status: TaskStatus.FAILED, error: timeoutMsg });
            if (task.requestId) {
              cleanupPendingTaskByTaskId(task.requestId);
            }
            applyHistoryUpdate(task, { status: 'failed', error: timeoutMsg });
            if (entry.timerId !== null) {
              clearInterval(entry.timerId);
            }
            delete pollingIntervals.current[task.id];
            inFlightTasks.current.delete(task.id);
            return;
          }
          await pollOnce();
        };

        void runOnceAndSchedule();

        const timer = setInterval(() => {
          entry.attempts += 1;
          if (entry.attempts > maxAttempts) {
            const timeoutMsg = '任务轮询超时（超过最大尝试次数）';
            applyTaskUpdateAndSync(task, { status: TaskStatus.FAILED, error: timeoutMsg });
            if (task.requestId) {
              cleanupPendingTaskByTaskId(task.requestId);
            }
            applyHistoryUpdate(task, { status: 'failed', error: timeoutMsg });
            if (entry.timerId !== null) {
              clearInterval(entry.timerId);
            }
            delete pollingIntervals.current[task.id];
            inFlightTasks.current.delete(task.id);
            return;
          }
          void pollOnce();
        }, intervalMs);

        entry.timerId = timer;
        pollingIntervals.current[task.id] = entry;
      })();
    });

    Object.entries(pollingIntervals.current).forEach(([taskId, entry]) => {
      const task = combinedTasks.find((t) => t.id === taskId);
      if (!task || task.status === TaskStatus.SUCCESS || task.status === TaskStatus.FAILED) {
        if (entry.timerId !== null) {
          clearInterval(entry.timerId);
        }
        delete pollingIntervals.current[taskId];
        inFlightTasks.current.delete(taskId);
      }
    });
  }, [combinedTasks, applyTaskUpdateAndSync, applyHistoryUpdate]);

  return null;
};
